import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { pick, difference } from 'lodash';
import { Observable } from 'rxjs';
import { BaseComponent } from 'app/components/base.component';
import { ToastrService } from 'ngx-toastr';
import { BsModalService } from 'ngx-bootstrap';
import { FormFieldType, ModalType } from 'app/components/modals/modal-form-template/modal-form.const';
import {
  buildCreateQCModel,
  buildEditQCModel,
  buildQCModelFilterMultipleTicket,
  buildQCModelFilterOneTicket,
  buildCreatePayloadSubmit,
  buildEditPayloadSubmit,
  FormFieldCode,
  ModalAction,
  buildTableColumns,
  TableDynamicAction,
  buildTableDynamicActions,
  getDisplayContentStatusColumn,
  getDisplayContentTimeColumn,
} from './campaign.util';
import {
  ViewCampaignQC,
  FilterType,
  GetQuery,
  QCStatus,
  SmartQcService,
  CampaignQC,
  FormQCModel,
} from 'app/common/services/SmartQC.service';
import { UserService } from 'app/common/services/User.service';
import { GroupService } from 'app/common/services/Group.service';
import { PriorityService } from 'app/common/services/Priority.service';
import { TicketService } from 'app/common/services/Ticket.service';
import { TableAction } from 'app/components/table-component/table.const';
import { Router } from '@angular/router';

@Component({
  selector: 'app-smart-qc-campaign',
  templateUrl: './campaign.component.html',
  styleUrls: ['./campaign.component.css'],
  providers: [UserService, GroupService, PriorityService, TicketService, SmartQcService],
})
export class CampaignComponent extends BaseComponent {
  @ViewChild('template') template: ElementRef;
  public tableColumns: any[];
  public tableDynamicButtons: any[] = [];
  public totalItem: number = 0;
  public totalPage: number = 0;
  public pageSize: number = 10;
  public currentPage: number = 1; // Set a default value;
  public priorityOptions: any = [];
  public agentOptions: any = [];
  public groupOptions: any = [];
  public ticketStatusOptions: any = [];
  public viewCampaignQCs: ViewCampaignQC[] = [];
  private _currentViewCampaignQC: ViewCampaignQC = null;
  public loading: boolean = false;
  public modalAction: ModalAction;

  constructor(
    protected changeDetector: ChangeDetectorRef,
    protected toastr: ToastrService,
    protected modalService: BsModalService,
    private router: Router,
    private userService: UserService,
    private groupService: GroupService,
    private priorityService: PriorityService,
    private ticketService: TicketService,
    private smartQCService: SmartQcService
  ) {
    super(changeDetector, toastr, modalService);
  }

  ngOnInit(): void {
    this.loading = true;
    this.tableColumns = buildTableColumns();
    this.tableDynamicButtons = buildTableDynamicActions();

    Observable.forkJoin([this.getPriority(), this.getAgents(), this.getGroups(), this.getTicketStatus()])
      .finally(() => {
        this.loading = false;
      })
      .subscribe(() => {
        this.getQCCampaigns();
      });
  }

  getPriority() {
    return this.priorityService.allPriority().map(({ status, data, error }) => {
      if (status === 200 && !error) {
        this.priorityOptions = data.map((item: any) => ({
          id: item.id,
          text: item.name,
        }));
      }
    });
  }

  getAgents() {
    return this.userService.all().map(({ status, data, error }) => {
      if (status === 200 && !error) {
        this.agentOptions = data.list.map((item: any) => ({
          id: item._id,
          text: item.name,
        }));
      }
    });
  }

  getGroups() {
    return this.groupService
      .allGroup({
        target: 'end_user',
      })
      .map(({ status, data, error }) => {
        if (status === 200 && !error) {
          this.groupOptions = data.map((item: any) => ({
            id: item.id,
            text: item.name,
          }));
        }
      });
  }

  getTicketStatus() {
    return this.ticketService.status().map(({ status, data, error }) => {
      if (status === 200 && !error) {
        this.ticketStatusOptions = data.map((item: any) => ({
          id: item.id.toString(),
          text: item.name,
        }));
      }
    });
  }

  getQCCampaigns() {
    this.loading = true;

    const query: GetQuery = {
      page: this.currentPage,
      page_size: this.pageSize,
    };

    return this.smartQCService
      .getCampaignQCs(query)
      .map(({ status, data, error }) => {
        if (status === 200 && !error) {
          const rowIndex = (this.currentPage - 1) * this.pageSize;

          return {
            ...data,
            campaignQCs: data.data.map((campaignQC: CampaignQC, index: number) => {
              return {
                ...campaignQC,
                index: rowIndex + index + 1,
                display_btn_view_campaign:
                  campaignQC.status === QCStatus.RUNNING || campaignQC.status === QCStatus.DONE,
                display_btn_execute_campaign: campaignQC.status === QCStatus.PENDING,
                display_btn_edit_campaign: campaignQC.status === QCStatus.PENDING,
                display_btn_view_report: campaignQC.status === QCStatus.DONE,
                display_status: getDisplayContentStatusColumn(campaignQC.status),
                display_time: getDisplayContentTimeColumn(campaignQC),
              };
            }),
          };
        }
      })
      .subscribe(data => {
        this.loading = false;
        this.totalItem = data.total;
        this.totalPage = Math.ceil(data.total / this.pageSize);
        this.viewCampaignQCs = data.campaignQCs;
      });
  }

  initModal() {
    this.modelContent = {
      type: ModalType.Primary,
      title: this.modalAction === ModalAction.ADD ? 'Thêm mới chiến dịch QC' : 'Cập nhật chiến dịch QC',
      submit_title: this.modalAction === ModalAction.ADD ? 'Thêm mới' : 'Cập nhật',
      close_title: 'Hủy bỏ',
    };

    const baseColumns = [
      {
        key: FormFieldCode.NAME,
        label: 'Tên chiến dịch',
        placeholder: 'Nhập tên chiến dịch',
        type: FormFieldType.Text,
        value: this.model[FormFieldCode.NAME],
        required: true,
        disabled: false,
        width: 'full',
        order: 0,
      },
      {
        key: FormFieldCode.DESCRIPTION,
        label: 'Mô tả chiến dịch',
        placeholder: 'Nhập mô tả chiến dịch',
        type: FormFieldType.Textarea,
        value: this.model[FormFieldCode.DESCRIPTION],
        required: false,
        disabled: false,
        width: 'full',
        order: 1,
      },
      {
        key: FormFieldCode.FILTER_TYPE,
        label: 'Điều kiện lọc',
        type: FormFieldType.Radio,
        value: this.model[FormFieldCode.FILTER_TYPE],
        required: false,
        disabled: false,
        width: 'full',
        order: 5,
        options: {
          data: [
            { id: 0, text: 'lọc theo 1 ticket' },
            { id: 1, text: 'Lọc theo nhiều ticket' },
          ],
        },
      },
    ];

    let filterColumns = [];
    if (this.model[FormFieldCode.FILTER_TYPE] === FilterType.ONE_TICKET) {
      filterColumns = [
        {
          key: FormFieldCode.CODE,
          label: 'Mã ticket',
          placeholder: 'Nhập mã ticket',
          type: FormFieldType.Text,
          value: this.model[FormFieldCode.CODE],
          required: false,
          disabled: false,
          width: 'full',
          order: 0,
        },
        {
          key: FormFieldCode.TICKET_FROM,
          label: 'Thời gian bắt đầu',
          type: FormFieldType.Datepicker,
          value: this.model[FormFieldCode.TICKET_FROM],
          required: false,
          disabled: false,
          width: '1/2',
          order: 8,
          placeholder: 'Thời gian bắt đầu',
          options: {
            placement: 'right',
          },
        },
        {
          key: FormFieldCode.TICKET_TO,
          label: 'Thời gian kết thúc',
          type: FormFieldType.Datepicker,
          value: this.model[FormFieldCode.TICKET_TO],
          required: false,
          disabled: false,
          width: '1/2',
          order: 9,
          placeholder: 'Thời gian kết thúc',
          options: {
            placement: 'right',
          },
        },
        {
          key: FormFieldCode.CALL_DURATION_FROM,
          label: 'Thời lượng (Từ)',
          type: FormFieldType.Timepicker,
          value: this.model[FormFieldCode.CALL_DURATION_FROM],
          required: false,
          disabled: false,
          width: '1/2',
          order: 8,
          placeholder: 'Từ',
          options: {
            placement: 'right',
          },
        },
        {
          key: FormFieldCode.CALL_DURATION_TO,
          label: 'Thời lượng (Đến)',
          type: FormFieldType.Timepicker,
          value: this.model[FormFieldCode.CALL_DURATION_TO],
          required: false,
          disabled: false,
          width: '1/2',
          order: 9,
          placeholder: 'Đến',
          options: {
            placement: 'right',
          },
        },
        {
          key: FormFieldCode.CALL_STATUS,
          label: 'Trạng thái trả lời',
          type: FormFieldType.Select2,
          value: this.model[FormFieldCode.CALL_STATUS],
          required: false,
          disabled: false,
          width: '1/2',
          order: 2,
          options: {
            data: [{ id: 'answer', text: 'Trả lời' }],
            select2Options: {
              placeholder: {
                id: '-1',
                text: 'Chọn trạng thái trả lời',
              },
            },
          },
        },
        {
          key: FormFieldCode.CALL_DIRECTION,
          label: 'Loại cuộc gọi',
          type: FormFieldType.CheckBox,
          value: this.model[FormFieldCode.CALL_DIRECTION],
          required: false,
          disabled: false,
          width: '1/2',
          order: 2,
          options: {
            data: [
              { id: 0, text: 'Inbound' },
              { id: 1, text: 'Outbound' },
            ],
            style: {
              'justify-content': 'space-around',
            },
            checkInline: true,
          },
        },
        {
          key: FormFieldCode.MAXIMUM_AUDIO,
          label: 'Số cuộc gọi dự kiến',
          type: FormFieldType.Number,
          value: this.model[FormFieldCode.MAXIMUM_AUDIO],
          required: false,
          disabled: false,
          width: '1/2',
          order: -1,
          placeholder: 'Số cuộc gọi dự kiến',
          options: {
            placement: 'left',
          },
        },
      ];
    }

    if (this.model[FormFieldCode.FILTER_TYPE] === FilterType.MULTIPLE_TICKET) {
      filterColumns = [
        {
          key: FormFieldCode.TICKET_FROM,
          label: 'Thời gian bắt đầu',
          type: FormFieldType.Datepicker,
          value: this.model[FormFieldCode.TICKET_FROM],
          required: false,
          disabled: false,
          width: '1/2',
          order: 8,
          placeholder: 'Thời gian bắt đầu',
          options: {
            placement: 'right',
          },
        },
        {
          key: FormFieldCode.TICKET_TO,
          label: 'Thời gian kết thúc',
          type: FormFieldType.Datepicker,
          value: this.model[FormFieldCode.TICKET_TO],
          required: false,
          disabled: false,
          width: '1/2',
          order: 9,
          placeholder: 'Thời gian kết thúc',
          options: {
            placement: 'right',
          },
        },
        {
          key: FormFieldCode.TICKET_FROM_TIME,
          label: 'Thời gian (Từ)',
          type: FormFieldType.Timepicker,
          value: this.model[FormFieldCode.TICKET_FROM_TIME],
          required: false,
          disabled: false,
          width: '1/2',
          order: 8,
          placeholder: 'Từ',
          options: {
            placement: 'right',
          },
        },
        {
          key: FormFieldCode.TICKET_TO_TIME,
          label: 'Thời gian (Đến)',
          type: FormFieldType.Timepicker,
          value: this.model[FormFieldCode.TICKET_TO_TIME],
          required: false,
          disabled: false,
          width: '1/2',
          order: 9,
          placeholder: 'Đến',
          options: {
            placement: 'right',
          },
        },
        {
          key: FormFieldCode.CALL_DURATION_FROM,
          label: 'Thời lượng (Từ)',
          type: FormFieldType.Timepicker,
          value: this.model[FormFieldCode.CALL_DURATION_FROM],
          required: false,
          disabled: false,
          width: '1/2',
          order: 8,
          placeholder: 'Từ',
          options: {
            placement: 'right',
          },
        },
        {
          key: FormFieldCode.CALL_DURATION_TO,
          label: 'Thời lượng (Đến)',
          type: FormFieldType.Timepicker,
          value: this.model[FormFieldCode.CALL_DURATION_TO],
          required: false,
          disabled: false,
          width: '1/2',
          order: 9,
          placeholder: 'Đến',
          options: {
            placement: 'right',
          },
        },
        {
          key: FormFieldCode.ASSIGN_AGENT_ID,
          label: 'Agent',
          type: FormFieldType.Select2,
          value: this.model[FormFieldCode.ASSIGN_AGENT_ID],
          required: false,
          disabled: false,
          width: '1/2',
          order: 2,
          options: {
            data: this.agentOptions,
            select2Options: {
              multiple: true,
              placeholdeR: {
                id: -1,
                text: 'Chọn chuyên viên',
              },
            },
          },
        },
        {
          key: FormFieldCode.ASSIGN_GROUP_ID,
          label: 'Bộ phận',
          type: FormFieldType.Select2,
          value: this.model[FormFieldCode.ASSIGN_GROUP_ID],
          required: false,
          disabled: false,
          width: '1/2',
          order: 2,
          options: {
            data: this.groupOptions,
            select2Options: {
              multiple: true,
              placeholdeR: {
                id: -1,
                text: 'Chọn phòng ban',
              },
            },
          },
        },
        {
          key: FormFieldCode.STATUS,
          label: 'Trạng thái',
          type: FormFieldType.Select2,
          value: this.model[FormFieldCode.STATUS],
          required: false,
          disabled: false,
          width: '1/2',
          order: 2,
          options: {
            data: this.ticketStatusOptions,
            select2Options: {
              multiple: true,
              placeholdeR: {
                id: -1,
                text: 'Chọn trạng thái ticket',
              },
            },
          },
        },
        {
          key: FormFieldCode.PRIORITY_ID,
          label: 'Độ ưu tiên',
          type: FormFieldType.Select2,
          value: this.model[FormFieldCode.PRIORITY_ID],
          required: false,
          disabled: false,
          width: '1/2',
          order: 2,
          options: {
            data: this.priorityOptions,
            select2Options: {
              multiple: true,
              placeholdeR: {
                id: -1,
                text: 'Chọn độ ưu tiên',
              },
            },
          },
        },
        {
          key: FormFieldCode.MAXIMUM_AUDIO,
          label: 'Số cuộc gọi dự kiến',
          type: FormFieldType.Number,
          value: this.model[FormFieldCode.MAXIMUM_AUDIO],
          required: false,
          disabled: false,
          width: '1/2',
          order: -1,
          placeholder: 'Số cuộc gọi dự kiến',
        },
        {
          key: FormFieldCode.CALL_DIRECTION,
          label: 'Loại cuộc gọi',
          type: FormFieldType.CheckBox,
          value: this.model[FormFieldCode.CALL_DIRECTION],
          required: false,
          disabled: false,
          width: '1/2',
          order: 2,
          options: {
            data: [
              { id: 0, text: 'Inbound' },
              { id: 1, text: 'Outbound' },
            ],
            style: {
              'justify-content': 'space-around',
            },
            checkInline: true,
          },
        },
      ];
    }

    this.modelDisplay = [...baseColumns, ...filterColumns];
  }

  handleOpenModal(action: ModalAction) {
    this.modalAction = action;
    this.model = action === ModalAction.ADD ? buildCreateQCModel() : buildEditQCModel(this._currentViewCampaignQC);

    this.initModal();

    this.openModal(
      this.template,
      {
        backdrop: true,
        ignoreBackdropClick: true,
        class: 'modal-xl',
        keyboard: false,
      },
      {},
      {
        modelContent: this.modelContent,
        modelDisplay: this.modelDisplay,
      }
    );
  }

  async handleTableAction(event: TableAction, campaignQC: ViewCampaignQC) {
    if (event === TableAction.delete) {
      const okDeleteQC = confirm('Bạn có muốn xóa chiến dịch này không');
      if (okDeleteQC) {
        this.smartQCService.deleteQCCampaign(campaignQC._id).subscribe(
          () => {
            this.throwSuccess('Xóa thành công');
            this.getQCCampaigns();
          },
          error => {
            this.throwError(error.message);
          }
        );
      }
    }
  }

  async handleTableCustomAction(event: TableDynamicAction, campaignQC: ViewCampaignQC) {
    if (event === TableDynamicAction.EXECUTE_CAMPAIGN) {
      const okExecuteQC = confirm('Bạn có muốn phân tích chiến dịch này không');
      if (okExecuteQC) {
        this.smartQCService
          .executeQCCampaign({
            qc_campaign_id: campaignQC._id,
          })
          .subscribe(
            () => {
              this.throwSuccess('Chạy chiến dịch thành công');
              this.getQCCampaigns();
            },
            error => {
              this.throwError(error.message);
            }
          );
      }
    } else if (event === TableDynamicAction.VIEW_REPORT) {
      this.router.navigate(['/smart-qc/report']);
    } else if (event === TableDynamicAction.EDIT_CAMPAIGN) {
      this._currentViewCampaignQC = campaignQC;
      this.handleOpenModal(ModalAction.EDIT);
    } else if (event === TableDynamicAction.VIEW_CAMPAIGN) {
      this._currentViewCampaignQC = campaignQC;
      this.router.navigate(['/smart-qc/campaigns', this._currentViewCampaignQC._id, 'calls']);
    }
  }

  handleCloseModal() {
    this.modalAction = ModalAction.CLOSE;
    this.closeModal();
  }

  handleClickRow(item: ViewCampaignQC) {
    // this.router.navigate(['/smart-qc/campaigns', item._id, 'calls']);
  }

  async handleSubmit(model: any) {
    let observable: Observable<any>;

    // const maximumAudio = model.maximum_audio;
    // if (!/^(?:[1-9][0-9])$/.test(maximumAudio)) {
    //   this.throwError('Số cuộc gọi tối đa phải là số và từ 1 cuộc gọi trở lên');
    //   return;
    // }
    if (model.filter_type === 0 && !model.code) {
      this.throwError('Trường mã ticket là bắt buộc khi với 1 ticket');
      return;
    }

    if (this.modalAction === ModalAction.ADD) {
      // console.log(buildCreatePayloadSubmit(model));
      observable = this.smartQCService.createCampaignQC(buildCreatePayloadSubmit(model));
    } else if (this.modalAction === ModalAction.EDIT) {
      observable = this.smartQCService.updateCampaignQC(
        this._currentViewCampaignQC._id,
        buildEditPayloadSubmit(model, this._currentViewCampaignQC)
      );

      // console.log(buildEditPayloadSubmit(model, this.currentViewCampaignQC));
    }

    observable.subscribe(
      () => {
        const message = this.modalAction === ModalAction.ADD ? 'Thêm mới thành công' : 'Cập nhật thành công';
        this.throwSuccess(message);
        this.getQCCampaigns();
        this.handleCloseModal();
      },
      (error: { message: string }) => {
        this.message = {
          class: 'alert dark alert-danger',
          content: error.message,
        };

        setTimeout(() => {
          this.message = null;
        }, 3000);
      }
    );
  }

  async onChangeValueModel(fieldCode: FormFieldCode, campaignQC: FormQCModel) {
    switch (fieldCode) {
      case FormFieldCode.FILTER_TYPE: {
        const baseModel = pick(campaignQC, [
          FormFieldCode.NAME,
          FormFieldCode.DESCRIPTION,
          FormFieldCode.FILTER_TYPE,
          FormFieldCode.MAXIMUM_AUDIO,
        ]);

        if (this.modalAction === ModalAction.ADD) {
          const filterModel =
            campaignQC[FormFieldCode.FILTER_TYPE] === FilterType.ONE_TICKET
              ? buildQCModelFilterOneTicket()
              : buildQCModelFilterMultipleTicket();

          this.model = {
            ...baseModel,
            ...filterModel,
          };
        }

        if (this.modalAction === ModalAction.EDIT) {
          let oldFilterModel =
            this._currentViewCampaignQC.filter_type === FilterType.ONE_TICKET
              ? buildQCModelFilterOneTicket(this._currentViewCampaignQC)
              : buildQCModelFilterMultipleTicket(this._currentViewCampaignQC);

          let newFilterModel =
            campaignQC.filter_type === FilterType.ONE_TICKET
              ? buildQCModelFilterOneTicket()
              : buildQCModelFilterMultipleTicket();

          let filterModel =
            campaignQC.filter_type === this._currentViewCampaignQC.filter_type ? oldFilterModel : newFilterModel;

          this.model = {
            ...baseModel,
            ...filterModel,
          };
        }

        break;
      }

      case FormFieldCode.CALL_DIRECTION: {
        const previousCallDirection = this.model[FormFieldCode.CALL_DIRECTION];
        const nextCallDirection = campaignQC.call_direction.map(value => parseInt(value as any));
        let callDirection: any;
        if (!previousCallDirection.length) {
          callDirection = nextCallDirection;
        } else {
          const newCallDirection = difference(nextCallDirection, previousCallDirection);

          callDirection = newCallDirection;
        }

        campaignQC.call_direction = callDirection;

        this.model = campaignQC;

        break;
      }

      default: {
        this.model = campaignQC;
        break;
      }
    }

    this.initModal();
  }

  setPage(page: number) {
    let newPage: number = page;
    if (page < 1) {
      newPage = 1;
    }

    if (page > this.totalPage) {
      newPage = this.totalPage;
    }

    if (newPage !== this.currentPage) {
      this.currentPage = newPage;
      this.getQCCampaigns();
    }
  }

  goPage2(page: string) {
    let toPage: any = page;
    if (!toPage) {
      toPage = 1;
    }

    if (Number.isInteger(toPage) && toPage <= 0) {
      toPage = 1;
    }

    this.setPage(toPage);
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/admin/icontact/smart-qc/campaign/campaign.component.ts